import { useCallback, useEffect, useMemo, useRef, useState } from "react";

import { CORE } from "../../lib/api";
import { ApiV2Error } from "../../lib/api-v2";
import { streamSse } from "../../lib/sse";
import { useDemoMode } from "../../lib/demo-mode";
import { DEMO_SUPERVISED_CALLS } from "../../lib/demo-data";
import { fetchSupervisedCalls } from "./supervision-api";
import type {
  Headroom,
  Shift,
  StreamAuthorization,
  StreamStatus,
  SupervisedCall,
} from "./types";

const POLL_INTERVAL_MS = 5000;
const STREAM_RETRY_MS = 8000;

/**
 * One SSE connection carrying every live call in the operator's org.
 *
 * Deliberately NOT one LiveKit room per call — watching is text-only. Audio is a later
 * phase and a deliberate, single-call action; twenty peer connections in one tab is how a
 * supervisor console becomes unusable.
 *
 * Resilience mirrors LiveCallContext, which is battle-tested here: stream first, fall back
 * to polling when it cannot be established or dies, so the screen is never silently stale.
 *
 * Ordering deliberately does NOT happen here. The page owns it, because it also owns which
 * tile is pinned and when a re-sort is allowed to happen -- re-ranking on every arriving
 * transcript line would shuffle the wall under the supervisor's pointer.
 */
export function useSupervisionStream(enabled = true) {
  const isDemoMode = useDemoMode();
  const [callsById, setCallsById] = useState<Map<string, SupervisedCall>>(() => new Map());
  const [endedCalls, setEndedCalls] = useState<Record<string, Partial<SupervisedCall>>>({});
  const [status, setStatus] = useState<StreamStatus>("idle");
  const [error, setError] = useState<string | null>(null);
  const [lastUpdatedAt, setLastUpdatedAt] = useState<string | null>(null);
  // Surfaced rather than merely logged: the audio session is a *separate* connection the
  // server cannot close, so something above this hook has to act on a revocation.
  const [authorization, setAuthorization] = useState<StreamAuthorization>("ok");
  // The rota rides the snapshot, so an on-shift console sees arrivals and departures on
  // the same 20s tick as the calls -- without a second poller of its own.
  const [rota, setRota] = useState<{ onShift: Shift[]; headroom: Headroom | null }>({
    onShift: [],
    headroom: null,
  });

  // Guards against a torn-down effect writing state after unmount.
  const aliveRef = useRef(false);

  const applySnapshot = useCallback((calls: SupervisedCall[]) => {
    const next = new Map<string, SupervisedCall>();
    for (const call of calls ?? []) {
      if (call?.call_id) next.set(call.call_id, call);
    }
    setCallsById(next);
    setLastUpdatedAt(new Date().toISOString());
  }, []);

  const upsertCall = useCallback((call: SupervisedCall) => {
    if (!call?.call_id) return;
    setCallsById((previous) => new Map(previous).set(call.call_id, call));
    setLastUpdatedAt(new Date().toISOString());
  }, []);

  const removeCall = useCallback((callId?: string) => {
    if (!callId) return;
    setCallsById((previous) => {
      if (!previous.has(callId)) return previous;
      const next = new Map(previous);
      next.delete(callId);
      return next;
    });
    setLastUpdatedAt(new Date().toISOString());
  }, []);

  useEffect(() => {
    if (!enabled) {
      setCallsById(new Map());
      setStatus("idle");
      return;
    }

    aliveRef.current = true;
    let handle: { close: () => void } | null = null;
    let pollTimer: ReturnType<typeof setInterval> | null = null;
    let retryTimer: ReturnType<typeof setTimeout> | null = null;
    let stopped = false;

    const stopPolling = () => {
      if (pollTimer) {
        clearInterval(pollTimer);
        pollTimer = null;
      }
    };

    const stopEverything = () => {
      stopped = true;
      stopPolling();
      handle?.close();
      if (retryTimer) {
        clearTimeout(retryTimer);
        retryTimer = null;
      }
    };

    const pollOnce = async () => {
      try {
        const data = await fetchSupervisedCalls();
        if (!aliveRef.current) return;
        applySnapshot(data?.calls ?? []);
        // The fallback carries the rota too. Dropping it here made the status strip go
        // blank whenever the stream fell back to polling, which reads as "nobody is on
        // shift" at exactly the moment things are already degraded.
        setRota({ onShift: data?.on_shift ?? [], headroom: data?.headroom ?? null });
        // Reading the roster IS the authorization check, so a successful poll clears a
        // previous "unavailable". Without this the flag is a one-way latch, and one blip
        // would tear down every subsequent Listen the moment it connected.
        setAuthorization("ok");
        setError(null);
      } catch (caught) {
        if (!aliveRef.current) return;
        // 403 is a settled answer, not a blip: the account lacks supervision.view,
        // or the org has the supervision category disabled. Retrying forever would
        // hammer the API and leave the operator staring at a spinner, so stop and
        // say why.
        if (caught instanceof ApiV2Error && caught.status === 403) {
          stopEverything();
          setStatus("error");
          setError(caught.message || "Supervision is not enabled for this account.");
          return;
        }
        setError((caught as Error)?.message || "Could not load live calls");
      }
    };

    const startPolling = () => {
      if (pollTimer || stopped) return;
      setStatus("polling");
      void pollOnce();
      pollTimer = setInterval(() => void pollOnce(), POLL_INTERVAL_MS);
    };

    const scheduleRetry = () => {
      if (stopped || retryTimer) return;
      startPolling();
      retryTimer = setTimeout(() => {
        retryTimer = null;
        connect();
      }, STREAM_RETRY_MS);
    };

    function connect() {
      if (stopped) return;
      setStatus("connecting");
      handle = streamSse(`${CORE}/api/v2/supervision/stream`, undefined, (event, data) => {
        if (!aliveRef.current) return;
        if (event === "snapshot") {
          stopPolling();
          setStatus("live");
          setError(null);
          setAuthorization("ok");
          applySnapshot((data.calls as SupervisedCall[]) ?? []);
          setRota({
            onShift: (data.on_shift as Shift[]) ?? [],
            headroom: (data.headroom as Headroom) ?? null,
          });
        } else if (event === "call_update" && data.call) {
          setStatus("live");
          upsertCall(data.call as SupervisedCall);
        } else if (event === "call_ended") {
          if (data.confirmed === true) {
            const id = data.call_id as string;
            setEndedCalls(previous => ({...previous, [id]: {
              lifecycle: "ended_pending", ended_at: data.ended_at as string | undefined,
              end_observed_at: data.end_observed_at as string | undefined,
            }}));
          }
          removeCall(data.call_id as string);
        } else if (event === "unauthorized" || event === "stream_closed") {
          // The server re-checks permission on every roster refresh, and fails closed.
          //
          // Two event names for one thing: core closes the feed as `stream_closed` with
          // `access_revoked`. Matching only `unauthorized` meant revocation arrived here
          // as an ordinary stream end, so the console reconnect-looped against a feed it
          // was no longer allowed to read -- and `authorization` never left "ok", which
          // is what the page keys the audio drop off. Revoking a supervisor mid-call left
          // them listening.
          if (data.reason === "authorization_unavailable") {
            // It could not verify us, which is not the same as denying us. Keep trying --
            // but the caller still drops audio, because an unverifiable supervisor should
            // not stay on a customer's line.
            setAuthorization("unavailable");
            setError("Could not confirm your access — reconnecting.");
            scheduleRetry();
          } else {
            // A settled answer, not a blip: reconnecting would just 403 in a tight loop.
            setAuthorization("revoked");
            stopEverything();
            setStatus("error");
            setError("Your access to supervision was removed.");
          }
        } else if (event === "error" || event === "done") {
          // Server restart, proxy timeout, or a 403. Keep the screen fresh with polling
          // while we retry, rather than leaving the operator on stale calls.
          scheduleRetry();
        }
      });
    }

    connect();

    return () => {
      stopped = true;
      aliveRef.current = false;
      handle?.close();
      stopPolling();
      if (retryTimer) clearTimeout(retryTimer);
    };
  }, [enabled, applySnapshot, upsertCall, removeCall]);

  const calls = useMemo(() => Array.from(callsById.values()), [callsById]);

  /**
   * DEMO MODE ONLY: the full set of demo fill cards tagged with `is_demo: true`.
   * The page merges these with real calls when `isDemoMode` is true, padding the wall
   * up to the supervisor's comfortable load.
   * An empty array is returned in production — no runtime cost.
   */
  const demoFills = useMemo(
    () => isDemoMode ? (DEMO_SUPERVISED_CALLS as unknown as SupervisedCall[]) : [],
    [isDemoMode],
  );

  return { calls, endedCalls, status, error, lastUpdatedAt, authorization, rota, demoFills };
}
